import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { accountAPI } from '../services/api';
import * as staffAPI from '../services/staffAPI';
import { User, Mail, HelpCircle, Trash2, Settings, ChevronRight, LogOut, X, Edit3, Bell } from 'react-feather';
import EmailTemplatesModal from './EmailTemplatesModal';
import ContactSupportModal from './ContactSupportModal';
import { TextInput } from './ui';
import { BACKEND_CONFIG } from '../config/backendConfig';

// APS-253: "a player or parent replied to your note". The section shows only
// when the preferences response includes this type.
const REPLY_TYPE = 'coach_note_reply';
type PrefChannel = 'email' | 'push' | 'socket';
const REPLY_CHANNELS: Array<{ id: PrefChannel; label: string }> = [
  { id: 'email', label: 'Email' },
  { id: 'push', label: 'Phone app' },
  { id: 'socket', label: 'In the portal' },
];

// Plain words for a staff role, e.g. "org_admin" reads "Organization admin".
const staffRoleLabel = (role: string): string => {
  switch (role) {
    case 'org_admin': return 'Organization admin';
    case 'team_coach': return 'Team coach';
    case 'assistant_coach': return 'Assistant coach';
    case 'recruiting_coordinator': return 'Recruiting coordinator';
    case 'scout': return 'Scout';
    default: {
      const words = role.replace(/_/g, ' ').trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    }
  }
};

interface PrefsPayload {
  email_enabled?: boolean;
  push_enabled?: boolean;
  socket_enabled?: boolean;
  preferences?: Partial<Record<PrefChannel, Record<string, boolean>>>;
}

async function prefsRequest(token: string, path: string, body?: object): Promise<PrefsPayload> {
  const response = await fetch(`${BACKEND_CONFIG.baseURL}/notification-preferences${path}`, {
    method: body ? 'PUT' : 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : null),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw new Error(`notification-preferences ${response.status}`);
  const json = await response.json();
  return (json?.data || {}) as PrefsPayload;
}

function replyOn(prefs: PrefsPayload, channel: PrefChannel): boolean {
  return prefs[`${channel}_enabled`] !== false && prefs.preferences?.[channel]?.[REPLY_TYPE] !== false;
}

function withReply(prefs: PrefsPayload, channel: PrefChannel, on: boolean, channelOn?: boolean): PrefsPayload {
  return {
    ...prefs,
    ...(channelOn === undefined ? null : { [`${channel}_enabled`]: channelOn }),
    preferences: {
      ...prefs.preferences,
      [channel]: { ...(prefs.preferences?.[channel] || {}), [REPLY_TYPE]: on },
    },
  };
}

interface StaffAccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The name/phone form writes to PUT /staff/profile, which refuses anyone
   * without an active staff membership. A college coach shares this modal
   * (KGH-795) but not that endpoint, so they see their details read-only rather
   * than an Edit button that 403s. Defaults to the staff behaviour.
   */
  canEditProfile?: boolean;
}

const StaffAccountSettingsModal: React.FC<StaffAccountSettingsModalProps> = ({ isOpen, onClose, canEditProfile = true }) => {
  const { user, sessionToken, logout, setUser } = useAuth();
  const toast = useToast();

  // Return focus to the control that opened the modal when it closes.
  const openerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    return () => {
      const opener = openerRef.current;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [isOpen]);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [emailTemplatesOpen, setEmailTemplatesOpen] = useState(false);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);

  const [prefs, setPrefs] = useState<PrefsPayload | null>(null);
  const [prefPending, setPrefPending] = useState<Record<PrefChannel, boolean>>({ email: false, push: false, socket: false });

  useEffect(() => {
    if (isOpen && user) {
      setName(user.name || '');
      setPhone((user as any).phone || '');
      setIsEditing(false);
    }
  }, [isOpen, user]);

  const loadPrefs = useCallback(async () => {
    if (!sessionToken) return;
    try {
      setPrefs(await prefsRequest(sessionToken, ''));
    } catch {
      setPrefs(null);
    }
  }, [sessionToken]);

  useEffect(() => {
    if (isOpen) loadPrefs();
  }, [isOpen, loadPrefs]);

  const replyPrefsAvailable = !!prefs && prefs.preferences?.email?.[REPLY_TYPE] !== undefined;

  const toggleReplyPref = async (channel: PrefChannel) => {
    if (!prefs || !sessionToken || prefPending[channel]) return;
    const next = !replyOn(prefs, channel);
    const channelWasOn = prefs[`${channel}_enabled`] !== false;
    const typeWasOn = prefs.preferences?.[channel]?.[REPLY_TYPE] !== false;
    setPrefPending((p) => ({ ...p, [channel]: true }));
    setPrefs((p) => (p ? withReply(p, channel, next, next ? true : undefined) : p));
    try {
      if (next && !channelWasOn) {
        await prefsRequest(sessionToken, '/channel', { channel, enabled: true });
      }
      await prefsRequest(sessionToken, '/individual', { channel, type: REPLY_TYPE, enabled: next });
    } catch {
      setPrefs((p) => (p ? withReply(p, channel, typeWasOn, channelWasOn) : p));
      toast.error('That did not save. Try again.');
    } finally {
      setPrefPending((p) => ({ ...p, [channel]: false }));
    }
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be blank.');
      return;
    }
    try {
      setIsSaving(true);
      const res = await staffAPI.updateStaffProfile({ name: name.trim(), phone: phone.trim() });
      // The header and the next open read the name from the session.
      if (user) {
        setUser({
          ...user,
          name: res?.name ?? name.trim(),
          phone: res?.phone ?? phone.trim(),
        } as typeof user);
      }
      setIsEditing(false);
      toast.success('Profile saved.');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!sessionToken) return;
    try {
      setIsDeleting(true);
      await accountAPI.deleteAccount(sessionToken);
      toast.success('Account scheduled for deletion.');
      setDeleteConfirmOpen(false);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete account.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  const menuItems = [
    { icon: <Mail size={18} />, label: 'Email Templates', subtitle: 'Create and manage reusable templates', onClick: () => setEmailTemplatesOpen(true) },
    { icon: <HelpCircle size={18} />, label: 'Contact Support', subtitle: 'Get help or report an issue', onClick: () => setContactSupportOpen(true) },
    { icon: <Trash2 size={18} />, label: 'Delete Account', subtitle: 'Permanently remove your account', onClick: () => setDeleteConfirmOpen(true), danger: true },
  ];

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#F8FAFC', borderRadius: '16px', width: '100%', maxWidth: '520px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: 'Manrope, sans-serif',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 24px', background: '#fff', borderBottom: '1px solid #E5E7EB',
          }}>
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937' }}>Staff Settings</span>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              display: 'flex', alignItems: 'center', color: '#6B7280',
            }}><X size={20} /></button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            {/* Profile Section */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(249,85,13,0.08)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <User size={18} color="#f9550d" />
                </div>
                <span style={{ flex: 1, fontSize: '16px', fontWeight: '700', color: '#1F2937' }}>My Profile</span>
                {canEditProfile && !isEditing && (
                  <button onClick={() => setIsEditing(true)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600', color: '#f9550d', fontFamily: 'Manrope, sans-serif',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}><Edit3 size={14} /> Edit</button>
                )}
              </div>

              <div style={{
                background: '#fff', borderRadius: '12px', padding: '16px',
                border: '1px solid #F3F4F6',
              }}>
                <label htmlFor="staff-settings-name" style={fieldLabelStyle}>Full Name</label>
                <TextInput
                  id="staff-settings-name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  disabled={!isEditing}
                  placeholder="Your name"
                  style={{ ...inputStyle, ...(isEditing ? {} : disabledInputStyle) }}
                />

                <label htmlFor="staff-settings-phone" style={{ ...fieldLabelStyle, marginTop: '14px' }}>Phone</label>
                <TextInput
                  id="staff-settings-phone"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  disabled={!isEditing}
                  placeholder="Your phone number"
                  style={{ ...inputStyle, ...(isEditing ? {} : disabledInputStyle) }}
                />

                {user && (
                  <>
                    <label style={{ ...fieldLabelStyle, marginTop: '14px' }}>Email</label>
                    <div style={{ ...inputStyle, ...disabledInputStyle, display: 'flex', alignItems: 'center' }}>
                      {user.email}
                    </div>
                  </>
                )}

                {(user as any)?.org_name && (
                  <>
                    <label style={{ ...fieldLabelStyle, marginTop: '14px' }}>Organization</label>
                    <div style={{ ...inputStyle, ...disabledInputStyle, display: 'flex', alignItems: 'center' }}>
                      {(user as any).org_name}
                    </div>
                  </>
                )}

                {(user as any)?.staff_role && (
                  <>
                    <label style={{ ...fieldLabelStyle, marginTop: '14px' }}>Role</label>
                    <div style={{ ...inputStyle, ...disabledInputStyle, display: 'flex', alignItems: 'center' }}>
                      {staffRoleLabel(String((user as any).staff_role))}
                    </div>
                  </>
                )}

                {isEditing && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                    <button
                      onClick={() => {
                        setName(user?.name || '');
                        setPhone((user as any)?.phone || '');
                        setIsEditing(false);
                      }}
                      style={{
                        padding: '8px 20px', borderRadius: '8px', border: '1px solid #E5E7EB',
                        background: '#fff', fontSize: '14px', fontWeight: '600', color: '#6B7280',
                        cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
                      }}
                    >Cancel</button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      style={{
                        padding: '8px 24px', borderRadius: '8px', border: 'none',
                        background: '#f9550d', fontSize: '14px', fontWeight: '700', color: '#fff',
                        cursor: isSaving ? 'wait' : 'pointer', fontFamily: 'Manrope, sans-serif',
                        opacity: isSaving ? 0.6 : 1,
                      }}
                    >{isSaving ? 'Saving...' : 'Save'}</button>
                  </div>
                )}
              </div>
            </div>

            {/* Notifications (APS-253) */}
            {replyPrefsAvailable && prefs && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'rgba(249,85,13,0.08)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Bell size={18} color="#f9550d" />
                  </div>
                  <span id="staff-settings-notif-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937' }}>Notifications</span>
                </div>
                <p id="staff-settings-notif-desc" style={{ margin: '0 0 12px 42px', fontSize: '13px', color: '#6B7280' }}>
                  When a player or parent replies to a note you wrote.
                </p>
                <div role="group" aria-labelledby="staff-settings-notif-title" aria-describedby="staff-settings-notif-desc">
                  {REPLY_CHANNELS.map((c) => {
                    const on = replyOn(prefs, c.id);
                    const busy = prefPending[c.id];
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="switch"
                        aria-checked={on}
                        aria-disabled={busy || undefined}
                        onClick={() => { if (!busy) toggleReplyPref(c.id); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: '12px', minHeight: 48, background: '#fff', borderRadius: '12px', padding: '12px 14px',
                          marginBottom: '8px', border: '1px solid #F3F4F6',
                          cursor: busy ? 'progress' : 'pointer', fontFamily: 'Manrope, sans-serif', textAlign: 'left',
                        }}
                      >
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>{c.label}</span>
                        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: on ? '#1F2937' : '#6B7280', minWidth: 24 }}>
                            {on ? 'On' : 'Off'}
                          </span>
                          <span style={{
                            position: 'relative', width: 40, height: 24, borderRadius: 999,
                            background: on ? '#151133' : '#D1D5DB', opacity: busy ? 0.6 : 1,
                            transition: 'background 0.15s',
                          }}>
                            <span style={{
                              position: 'absolute', top: 3, left: on ? 19 : 3, width: 18, height: 18,
                              borderRadius: 999, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                              transition: 'left 0.15s',
                            }} />
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Menu Items */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(249,85,13,0.08)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <Settings size={18} color="#f9550d" />
                </div>
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937' }}>Manage</span>
              </div>

              {menuItems.map((item, i) => (
                <button
                  key={i}
                  onClick={item.onClick}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    background: '#fff', borderRadius: '12px', padding: '14px',
                    marginBottom: '8px', border: item.danger ? '1px solid rgba(220,38,38,0.1)' : '1px solid #F3F4F6',
                    cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: item.danger ? 'rgba(220,38,38,0.06)' : 'rgba(249,85,13,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px',
                    color: item.danger ? '#dc2626' : '#f9550d',
                  }}>{item.icon}</div>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{
                      fontSize: '15px', fontWeight: '600',
                      color: item.danger ? '#dc2626' : '#1F2937',
                    }}>{item.label}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '1px' }}>{item.subtitle}</div>
                  </div>
                  <ChevronRight size={18} color="#9CA3AF" />
                </button>
              ))}
            </div>

            {/* App Info */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>APS Recruits · Staff</div>
            </div>

            {/* Sign Out */}
            <button
              onClick={() => setSignOutConfirmOpen(true)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '14px', borderRadius: '12px',
                border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)',
                cursor: 'pointer', fontFamily: 'Manrope, sans-serif', marginBottom: '20px',
              }}
            >
              <LogOut size={18} color="#dc2626" />
              <span style={{ fontSize: '15px', fontWeight: '700', color: '#dc2626' }}>Sign out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sub-Modals */}
      <EmailTemplatesModal isOpen={emailTemplatesOpen} onClose={() => setEmailTemplatesOpen(false)} />
      <ContactSupportModal isOpen={contactSupportOpen} onClose={() => setContactSupportOpen(false)} />

      {/* Delete Account Confirmation */}
      {deleteConfirmOpen && (
        <div
          onClick={() => setDeleteConfirmOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '400px',
            width: '90%', fontFamily: 'Manrope, sans-serif',
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1F2937' }}>Delete Account?</h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>
              This will schedule your account for permanent deletion. You'll have 30 days to reactivate before data is removed.
            </p>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#374151' }}>
              Type <strong>DELETE</strong> to confirm:
            </p>
            <TextInput
              value={deleteText} onChange={(e) => setDeleteText(e.target.value)}
              placeholder="Type DELETE"
              aria-label="Type DELETE to confirm"
              style={{ ...inputStyle, marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setDeleteConfirmOpen(false); setDeleteText(''); }} style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid #E5E7EB',
                background: '#fff', fontSize: '14px', fontWeight: '600', color: '#6B7280',
                cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
              }}>Cancel</button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteText !== 'DELETE' || isDeleting}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: 'none',
                  background: deleteText === 'DELETE' ? '#dc2626' : '#FCA5A5',
                  fontSize: '14px', fontWeight: '700', color: '#fff',
                  cursor: deleteText === 'DELETE' && !isDeleting ? 'pointer' : 'not-allowed',
                  fontFamily: 'Manrope, sans-serif', opacity: isDeleting ? 0.6 : 1,
                }}
              >{isDeleting ? 'Deleting...' : 'Delete Account'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Out Confirmation */}
      {signOutConfirmOpen && (
        <div
          onClick={() => setSignOutConfirmOpen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '380px',
            width: '90%', fontFamily: 'Manrope, sans-serif',
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#1F2937' }}>Sign out</h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6B7280' }}>Are you sure you want to sign out?</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setSignOutConfirmOpen(false)} style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid #E5E7EB',
                background: '#fff', fontSize: '14px', fontWeight: '600', color: '#6B7280',
                cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
              }}>Cancel</button>
              <button onClick={async () => { try { await logout(); } catch {} }} style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                background: '#dc2626', fontSize: '14px', fontWeight: '700', color: '#fff',
                cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
              }}>Sign out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: '12px', fontWeight: '600', color: '#6B7280',
  marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px',
};

/* Shared by the three text boxes AND by the read-only <div>s that stand in for
 * email/org/role, which is why it still carries its own border and fill. No
 * `outline`: inline, it outranked the global :focus-visible ring and left every
 * box in this sheet with no keyboard focus indicator. */
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #E5E7EB',
  borderRadius: '8px', fontSize: '15px', color: '#1F2937',
  fontFamily: 'Manrope, sans-serif', boxSizing: 'border-box',
  background: '#fff',
};

const disabledInputStyle: React.CSSProperties = {
  background: '#F9FAFB', borderColor: '#F3F4F6', color: '#6B7280',
};

export default StaffAccountSettingsModal;
